/**
 * Criterion Industries - Architect Specification Landing Page JavaScript
 * Handles navigation, smooth scrolling, and interactive elements
 */

// ============================================
// GLOBAL VARIABLES
// ============================================
let isScrolling = false;
let currentSection = '';

// Document-space offset of an element.
// offsetTop is measured from the nearest *positioned* ancestor. The theme wraps
// page templates in .container.main.content, which carries `position: relative`
// and sits ~74px down the page, so every scroll target came out 74px short and
// each section heading landed tucked under the sticky nav. Measuring from the
// viewport rect plus current scroll is independent of any positioned ancestor.
function docOffsetTop(el) {
    return el.getBoundingClientRect().top + window.pageYOffset;
}

// ============================================
// SMOOTH SCROLL FUNCTIONALITY
// ============================================

/**
 * Initialize smooth scrolling for anchor links
 */
function initSmoothScroll() {
    // Handle all anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault();
            
            const targetId = this.getAttribute('href').substring(1);
            const targetElement = document.getElementById(targetId);
            
            if (targetElement) {
                // Calculate offset for sticky navigation
                const navHeight = document.querySelector('.sticky-nav').offsetHeight;
                const targetPosition = docOffsetTop(targetElement) - navHeight - 20;
                
                // Smooth scroll to target
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
                
                // Update active navigation
                updateActiveNavigation(targetId);
            }
        });
    });
    
    // Special handler for access resources button
    const accessBtn = document.getElementById('access-resources-btn');
    if (accessBtn) {
        accessBtn.addEventListener('click', function() {
            const resourcesSection = document.getElementById('resources-section');
            if (resourcesSection) {
                const navHeight = document.querySelector('.sticky-nav').offsetHeight;
                const targetPosition = docOffsetTop(resourcesSection) - navHeight - 20;
                
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            }
        });
    }
}

// ============================================
// NAVIGATION FUNCTIONALITY
// ============================================

/**
 * Update active navigation link based on scroll position
 */
function updateActiveNavigationOnScroll() {
    const sections = document.querySelectorAll('section[id]');
    const navLinks = document.querySelectorAll('.nav-link');
    const scrollPosition = window.pageYOffset;
    
    sections.forEach(section => {
        const sectionTop = docOffsetTop(section) - 100;
        const sectionBottom = sectionTop + section.offsetHeight;
        const sectionId = section.getAttribute('id');
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            // Remove active class from all links
            navLinks.forEach(link => {
                link.classList.remove('active');
            });
            
            // Add active class to current section link
            const activeLink = document.querySelector(`.nav-link[href="#${sectionId}"]`);
            if (activeLink) {
                activeLink.classList.add('active');
            }
            
            currentSection = sectionId;
        }
    });
}

/**
 * Manually update active navigation
 */
function updateActiveNavigation(sectionId) {
    const navLinks = document.querySelectorAll('.nav-link');
    
    navLinks.forEach(link => {
        link.classList.remove('active');
        if (link.getAttribute('href') === `#${sectionId}`) {
            link.classList.add('active');
        }
    });
}

// ============================================
// SCROLL EFFECTS
// ============================================

/**
 * Handle scroll-based animations and effects
 */
function handleScrollEffects() {
    // Throttle scroll events
    if (!isScrolling) {
        window.requestAnimationFrame(function() {
            updateActiveNavigationOnScroll();
            isScrolling = false;
        });
        isScrolling = true;
    }
}

// ============================================
// INTERSECTION OBSERVER FOR ANIMATIONS
// ============================================

/**
 * Initialize intersection observer for fade-in animations
 */
function initAnimations() {
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('animate-in');
                
                // Add staggered animation for cards
                if (entry.target.classList.contains('stagger-animation')) {
                    const cards = entry.target.querySelectorAll('.card, .contact-card, .resource-card, .product-card');
                    cards.forEach((card, index) => {
                        setTimeout(() => {
                            card.style.opacity = '1';
                            card.style.transform = 'translateY(0)';
                        }, index * 100);
                    });
                }
            }
        });
    }, observerOptions);
    
    // Observe sections
    document.querySelectorAll('section').forEach(section => {
        observer.observe(section);
    });
    
    // Observe individual cards with initial hidden state
    document.querySelectorAll('.contact-card, .resource-card, .product-card').forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(card);
    });
}

// ============================================
// HOVER EFFECTS
// ============================================

/**
 * Initialize hover effects for cards
 */
function initHoverEffects() {
    // Product card image zoom effect
    document.querySelectorAll('.product-card').forEach(card => {
        const img = card.querySelector('img');
        if (img) {
            card.addEventListener('mouseenter', () => {
                img.style.transform = 'scale(1.08)';
            });
            card.addEventListener('mouseleave', () => {
                img.style.transform = 'scale(1)';
            });
        }
    });
}

// ============================================
// ANALYTICS TRACKING
// ============================================

/**
 * Track button clicks and interactions
 */
function initAnalytics() {
    // Track button clicks
    document.querySelectorAll('button, .btn-primary, .btn-hero-filled, .btn-hero-outline').forEach(button => {
        button.addEventListener('click', function() {
            const buttonText = this.textContent.trim();
            const section = this.closest('section');
            const sectionId = section ? section.id : 'unknown';
            
            // Track with Google Analytics if available
            if (typeof gtag !== 'undefined') {
                gtag('event', 'button_click', {
                    'button_text': buttonText,
                    'section': sectionId
                });
            }
            
            // Track with Shopify Analytics if available
            if (typeof Shopify !== 'undefined' && Shopify.analytics) {
                Shopify.analytics.track('Button Clicked', {
                    button: buttonText,
                    location: sectionId
                });
            }
        });
    });
    
    // Track navigation clicks
    document.querySelectorAll('.nav-link').forEach(link => {
        link.addEventListener('click', function() {
            const target = this.getAttribute('href').substring(1);
            
            if (typeof gtag !== 'undefined') {
                gtag('event', 'navigation_click', {
                    'target_section': target
                });
            }
        });
    });
}

// ============================================
// RESPONSIVE MENU
// ============================================

/**
 * Handle responsive navigation on mobile
 */
function initResponsiveNav() {
    const nav = document.querySelector('.sticky-nav');
    let lastScroll = 0;
    
    window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        
        // Hide/show nav on mobile based on scroll direction
        if (window.innerWidth <= 768) {
            if (currentScroll > lastScroll && currentScroll > 100) {
                // Scrolling down
                nav.style.transform = 'translateY(-100%)';
            } else {
                // Scrolling up
                nav.style.transform = 'translateY(0)';
            }
        }
        
        lastScroll = currentScroll;
    });
}

// ============================================
// EMAIL TRUNCATION FOR MOBILE
// ============================================

/**
 * Handle email display on different screen sizes
 */
function handleEmailDisplay() {
    const emailLinks = document.querySelectorAll('.email-link');
    
    function updateEmailDisplay() {
        const screenWidth = window.innerWidth;
        
        emailLinks.forEach(link => {
            const fullEmail = link.getAttribute('href').replace('mailto:', '');
            
            if (screenWidth < 480) {
                // Show truncated email on very small screens
                const [name, domain] = fullEmail.split('@');
                link.textContent = name.substring(0, 8) + '...@' + domain;
            } else {
                // Show full email
                link.textContent = fullEmail;
            }
        });
    }
    
    // Update on load and resize
    updateEmailDisplay();
    window.addEventListener('resize', updateEmailDisplay);
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Debounce function for performance
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Check if element is in viewport
 */
function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize all functionality when DOM is ready
 */
function initialize() {
    console.log('Initializing Architect Specification page...');
    
    // Core functionality
    initSmoothScroll();
    initAnimations();
    initHoverEffects();
    initAnalytics();
    initResponsiveNav();
    handleEmailDisplay();
    
    // Scroll event listeners
    window.addEventListener('scroll', handleScrollEffects);
    
    // Set initial active navigation
    updateActiveNavigationOnScroll();
    
    // Handle window resize
    window.addEventListener('resize', debounce(() => {
        handleEmailDisplay();
    }, 250));
    
    console.log('Initialization complete!');
}

// ============================================
// SHOPIFY SPECIFIC HANDLERS
// ============================================

/**
 * Handle Shopify-specific functionality
 */
function initShopifyHandlers() {
    // Wait for Shopify to be ready
    if (window.Shopify) {
        // Track page view
        if (Shopify.analytics) {
            Shopify.analytics.track('Viewed Architect Specification Page');
        }
        
        // Handle Shopify cart interactions if needed
        document.addEventListener('cart:updated', function(event) {
            console.log('Cart updated:', event.detail);
        });
    }
}

// ============================================
// START APPLICATION
// ============================================

// Check if DOM is already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
} else {
    // DOM is already loaded
    initialize();
}

// Initialize Shopify handlers when ready
if (window.Shopify) {
    initShopifyHandlers();
} else {
    // Wait for Shopify to load
    window.addEventListener('load', initShopifyHandlers);
}

// Export functions for external use if needed
window.criterionSpecification = {
    scrollToSection: function(sectionId) {
        const section = document.getElementById(sectionId);
        if (section) {
            const navHeight = document.querySelector('.sticky-nav').offsetHeight;
            const targetPosition = docOffsetTop(section) - navHeight - 20;
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    },
    updateActiveNav: updateActiveNavigation
};